import { useState, useEffect, useMemo, useCallback } from 'react';

import PageHeader from '../common/PageHeader';
import Button from '../common/Button';
import IconButton from '../common/IconButton';
import Spinner from '../common/Spinner';
import { List, ListItem, ListItemActions, ListItemText } from '../common/List';
import SongSearchDialog from '../common/SongSearchDialog';

import BanIcon from '../../icons/Ban';
import TrashIcon from '../../icons/Trash';

import { getSongMeta } from '../../utils/songFile';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import * as apiClient from '../../apiClient';

function SongLocksCall() {
  const perPage = 100;
  const [locks, setLocks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [isAddLockOpen, setIsAddLockOpen] = useState(false);

  const loadLocks = async () => {
    const response = await apiClient.getSongLocks({ page: 1, perPage });
    setPage(1);
    setLocks(response.items);
    setHasNext(response.hasNext);
    setTotal(response.total);
  };

  const paginate = useCallback(async () => {
    if (!hasNext) return;
    const newPage = page + 1;

    const response = await apiClient.getSongLocks({ page: newPage, perPage });

    setPage(newPage);
    setHasNext(response.hasNext);
    setTotal(response.total);
    setLocks((currentLocks) => [...currentLocks, ...response.items]);
  }, [hasNext, page]);

  const scrollRef = useIntersectionObserver({
    onIntersect: paginate,
  });

  useEffect(() => {
    loadLocks();
  }, []);

  const handleRemoveLock = (lockId) => async () => {
    await apiClient.unlockSong(lockId);
    setLocks((locks) => locks.filter((lock) => lock.id !== lockId));
    setTotal((currentTotal) => currentTotal - 1);
  };

  const handleAddLock = (song) => async () => {
    const newLock = await apiClient.lockSong({
      songId: song.id,
      collection: song.collection,
    });
    setLocks((locks) => [...locks, newLock]);
    setTotal((currentTotal) => currentTotal + 1);
  };

  const handleOpenAddLock = () => {
    setIsAddLockOpen(true);
  };

  const handleCloseAddLock = () => {
    setIsAddLockOpen(false);
    loadLocks();
  };

  const locksMap = useMemo(() => {
    const lockMap = new Map();
    locks.forEach((lock) => {
      lockMap.set(lock.songId, lock.id);
    });
    return lockMap;
  }, [locks]);

  return (
    <div>
      <PageHeader title="Bloqueio de Músicas" />
      <div className="p-6 md:px-10 md:pb-10 md:pt-0">
        <SongSearchDialog
          open={isAddLockOpen}
          onClose={handleCloseAddLock}
          renderActions={(song) =>
            locksMap.has(song.id) ? (
              <IconButton
                title="Remover bloqueio"
                color="primary"
                onClick={handleRemoveLock(locksMap.get(song.id))}
              >
                <BanIcon />
              </IconButton>
            ) : (
              <IconButton
                title="Adicionar bloqueio"
                onClick={handleAddLock(song)}
              >
                <BanIcon />
              </IconButton>
            )
          }
        />

        <div className="flex flex-col justify-between pb-5 border-b-2 md:flex-row md:items-center border-grayAlt">
          <div className="flex-1 mb-5 md:mb-0">
            <h2 className="text-2xl font-semibold">Seus bloqueios</h2>
            <p className="text-gray-400">{total} bloqueadas</p>
          </div>

          <Button color="primary" onClick={handleOpenAddLock}>
            Adicionar
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {locks.length === 0 ? (
            <p className="py-4">Nenhum bloqueio encontrado.</p>
          ) : (
            <List>
              {locks.map((lock) => {
                const { category, title, artist } = getSongMeta(lock);
                const secondaryText = artist
                  ? `${artist} • ${category}`
                  : category;

                return (
                  <ListItem key={`lock-${lock.id}`} title={lock.fileName}>
                    <ListItemText
                      primary={title}
                      secondary={secondaryText}
                      className="line-through"
                    />
                    <ListItemActions>
                      <IconButton onClick={handleRemoveLock(lock.id)}>
                        <TrashIcon />
                      </IconButton>
                    </ListItemActions>
                  </ListItem>
                );
              })}
            </List>
          )}
          {hasNext && (
            <div ref={scrollRef} className="flex justify-center">
              <Spinner className="w-6 my-6" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SongLocksCall;
